// Which user markers the dispatch map shows.
//
// Mirrors the mobile app's MapBloc.setUserLiveMarkers, which has exactly two
// modes and no third:
//
//   nothing selected  -> on-duty users of your OWN organisation
//   incident selected -> only that incident's acceptedBy responders, and
//                        nothing at all until someone has accepted
//
// The map was showing the union of both, and the two leaked into each other.
// Selecting an incident emits `join_user_location_update` for every acceptedBy
// user (DispatchMap ~1484); their positions arrive on `user_location_update`
// and land in deviceLocations, and DeviceMarkersWrapper rendered that object
// wholesale with no reference to the selection. So a selected incident showed
// org users alongside its crew, and going back to show-all still showed the
// crew, because their entries linger until the two-minute stale sweep.
//
// CommonJS on purpose: this is the one piece of dispatch-map logic that is pure
// and worth pinning, and selectMapDeviceLocations.check.js runs it under plain
// node with no CRA/jest machinery. Webpack imports it just the same.

/**
 * @param {Object} deviceLocations id -> { user, loc }
 * @param {Object|null} selectedIncident the open incident, if any
 * @param {Object|null} profile the signed-in user's document
 * @returns {Object} the subset to render
 */
function selectMapDeviceLocations(deviceLocations, selectedIncident, profile) {
  const entries = Object.entries(deviceLocations || {})

  if (selectedIncident) {
    const acceptedBy = Array.isArray(selectedIncident.acceptedBy)
      ? selectedIncident.acceptedBy
      : []
    const accepted = new Set(
      acceptedBy
        .map((a) => a && a.userId)
        .filter(Boolean)
        .map(String)
    )
    // Not yet accepted by anyone: the incident marker stands alone, same as
    // mobile, which only queries users when acceptedBy is non-empty.
    if (!accepted.size) return {}
    return Object.fromEntries(
      entries.filter(([, d]) => accepted.has(String(d && d.user && d.user.id)))
    )
  }

  // Show-all. Own organisation only.
  //
  // THE TYPE TRAP: organization.id is a NUMBER in Firestore while ids arrive as
  // strings from claims, URLs and socket payloads. `==` between 202509081156
  // and "202509081156" is false, and the whole map would silently go empty, so
  // both sides are stringified. See the header of firestore.rules.
  const myOrgId = profile && profile.organization && profile.organization.id
  if (myOrgId === undefined || myOrgId === null) return {}
  return Object.fromEntries(
    entries.filter(
      ([, d]) =>
        String(d && d.user && d.user.organization && d.user.organization.id) ===
        String(myOrgId)
    )
  )
}

module.exports = { selectMapDeviceLocations }
